import React, { useEffect, useState } from 'react';
import './CysteineTable.css';
import { getCellLineDataAsync } from '../data/cellLineData/loader';
import { loadSearchData, getGeneNameByProteinId } from '../utils/searchDataLoader';

interface CysteineData {
  identifier: string;
  altIdentifier: string;
  modifiedPeptide: string;
  log2Average: number;
  isHyperReactive: boolean;
}

interface CellLineData {
  cellLine: string;
  tissue: string;
  cysteines: Record<string, CysteineData>;
}

interface CysteineTableProps {
  proteinId: string;
  onClose: () => void;
}

const CysteineTable: React.FC<CysteineTableProps> = ({ proteinId, onClose }) => {
  const [cellLineData, setCellLineData] = useState<CellLineData[]>([]);
  const [cysteineColumns, setCysteineColumns] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [geneName, setGeneName] = useState<string | null>(null);

  useEffect(() => {
    loadProteinData();
  }, [proteinId]);
  
  const loadProteinData = async () => {
    // Load search data to get gene name
    await loadSearchData();
    const gene = getGeneNameByProteinId(proteinId);
    setGeneName(gene);
    
    // Load cell line data
    loadCellLineData();
  };

  // Simple CSV parser that handles quoted fields
  const parseCSVLine = (line: string): string[] => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  };

  const loadCellLineData = async () => {
    setLoading(true);
    const cellLines = [
      'Breast_BT-549', 'Breast_Hs-578T', 'Breast_MCF-7', 'Breast_MDA-MB-231',
      'Breast_MDA-MB-468', 'Breast_T-47D', 'CNS_SF-268', 'CNS_SF-295',
      'CNS_SF-539', 'CNS_SNB-19', 'CNS_SNB-75', 'CNS_U251',
      'Colon_COLO-205', 'Colon_HCC-2998', 'Colon_HCT-116', 'Colon_HCT-15',
      'Colon_HT29', 'Colon_SW-620', 'Leukemia_CCRF-CEM', 'Leukemia_HL-60',
      'Leukemia_K562', 'Leukemia_MOLT-4', 'Leukemia_RPMI-8226', 'Leukemia_SR',
      'Melanoma_LOX-IMVI', 'Melanoma_M14', 'Melanoma_MALME-3M', 'Melanoma_MDA-MB-435',
      'Melanoma_SK-MEL-28', 'Melanoma_SK-MEL-2', 'Melanoma_SK-MEL-5', 'Melanoma_UACC-257',
      'Melanoma_UACC-62', 'NSCL_A549', 'NSCL_EKVX', 'NSCL_HOP-62',
      'NSCL_HOP-92', 'NSCL_NCI-H226', 'NSCL_NCI-H23', 'NSCL_NCI-H322M',
      'NSCL_NCI-H460', 'NSCL_NCI-H522', 'Ovarian_ADR-RES', 'Ovarian_IGR-OV1',
      'Ovarian_OVCAR-3', 'Ovarian_OVCAR-4', 'Ovarian_OVCAR-5', 'Ovarian_OVCAR-8',
      'Ovarian_SK-OV-3', 'Prostate_DU145', 'Prostate_PC-3', 'Renal_786-0',
      'Renal_A498', 'Renal_ACHN', 'Renal_CAKI-1', 'Renal_RXF-393',
      'Renal_SN12C', 'Renal_TK-10', 'Renal_UO-31'
    ];

    const allCysteineIds = new Set<string>();
    const cellLineDataArray: CellLineData[] = [];

    // Load and parse data for each cell line
    for (const cellLine of cellLines) {
      try {
        const rows = await getCellLineDataAsync(cellLine);
        if (!rows || rows.length === 0) continue;
        
        const headers = rows[0];
        
        const [tissue, cellLineName] = cellLine.split('_');
        const cysteineData: Record<string, CysteineData> = {};
        
        for (let i = 1; i < rows.length; i++) {
          const values = rows[i];
          if (!values || values.length === 0) continue;
          
          const identifier = values[0];
          const altIdentifier = values[1];
          
          // Extract protein from identifier (e.g., "Q9UIC8_C258" -> "Q9UIC8")
          const [currentProtein] = identifier.split('_');
          
          // Only include cysteines for the selected protein
          if (currentProtein !== proteinId) continue;
          
          // Extract cysteine position (e.g., "C258")
          const cysteineId = identifier.split('_')[1];
          allCysteineIds.add(cysteineId);
          
          cysteineData[cysteineId] = {
            identifier,
            altIdentifier,
            modifiedPeptide: values[2],
            log2Average: parseFloat(values[3]) || 0,
            isHyperReactive: values[4] === 'True'
          };
        }
        
        cellLineDataArray.push({
          cellLine: cellLineName,
          tissue,
          cysteines: cysteineData
        });
      } catch (error) {
        console.error(`Error loading data for ${cellLine}:`, error);
      }
    }
    
    // Sort cysteine columns numerically
    const sortedCysteines = Array.from(allCysteineIds).sort((a, b) => {
      const numA = parseInt(a.substring(1));
      const numB = parseInt(b.substring(1));
      return numA - numB;
    });
    
    setCysteineColumns(sortedCysteines);
    setCellLineData(cellLineDataArray);
    setLoading(false);
  };

  const getCellColor = (value: number, isHyperReactive: boolean) => {
    if (isHyperReactive) {
      return '#ff6b6b'; // Red for hyper-reactive
    }
    
    // Color scale from blue (low) to white (mid) to yellow (high)
    const min = -2;
    const max = 8;
    const normalized = (value - min) / (max - min);
    
    if (normalized < 0.5) {
      // Blue to white
      const intensity = normalized * 2;
      return `rgb(${Math.floor(255 * intensity)}, ${Math.floor(255 * intensity)}, 255)`;
    } else {
      // White to yellow
      const intensity = (normalized - 0.5) * 2;
      return `rgb(255, 255, ${Math.floor(255 * (1 - intensity))})`;
    }
  };

  if (loading) {
    return (
      <div className="cysteine-table-overlay">
        <div className="cysteine-table-container">
          <div className="loading">Loading cysteine data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="cysteine-table-overlay" onClick={onClose}>
      <div className="cysteine-table-container" onClick={(e) => e.stopPropagation()}>
        <div className="table-header">
          <h2>Cysteine Data for Protein: {geneName ? `${geneName} (${proteinId})` : proteinId}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="table-wrapper">
          <table className="cysteine-table">
            <thead>
              <tr>
                <th className="cell-line-header">Cell Line</th>
                {cysteineColumns.map(cys => (
                  <th key={cys} className="cysteine-header">{cys}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cellLineData.map(({ cellLine, tissue, cysteines }) => (
                <tr key={cellLine}>
                  <td className="cell-line-cell">
                    <div className="cell-line-name">{cellLine}</div>
                    <div className="tissue-type">{tissue}</div>
                  </td>
                  {cysteineColumns.map(cys => {
                    const data = cysteines[cys];
                    if (!data) {
                      return <td key={cys} className="data-cell empty">-</td>;
                    }
                    return (
                      <td
                        key={cys}
                        className="data-cell"
                        style={{
                          backgroundColor: getCellColor(data.log2Average, data.isHyperReactive)
                        }}
                        title={`${data.altIdentifier}\nLog2 Average: ${data.log2Average.toFixed(2)}\n${data.isHyperReactive ? 'Hyper-reactive' : ''}`}
                      >
                        {data.log2Average.toFixed(1)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="table-legend">
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#ff6b6b' }}></span>
            <span>Hyper-reactive</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#0000ff' }}></span>
            <span>Low Log2 Ratio</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#ffff00' }}></span>
            <span>High Log2 Ratio</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CysteineTable;